import { createContext, useContext } from 'react';
import { createSelector, Selector } from '@reduxjs/toolkit';

import { SLOT_TYPES, SLOTS_PLACEMENT } from '@lootboy/lib/constants';
import { TSlot } from '@lootboy/lib/slices/slots';
// @ts-ignore lib
import slotsFilteredFactory from '@lootboy/lib/slices/slots/slotsFilteredFactory';
import { TStore } from '@lootboy/lib/types/store';

import { ILootpack } from '~/interfaces/ILootpack';

export type TSlotsContextState = {
  useFilter: (id: string) => [checked: boolean, setFilter: () => void];
  useSearch: () => [search: string, setSearch: (newSearch: string) => void];
  searchedLootsSelector: Selector<
    TStore,
    { loots: Array<ILootpack>; areFiltersSelected: boolean; search: string }
  >;
  selector: Selector<TStore, Array<TSlot>>;
  resetFiltersAndSearch: () => { payload: unknown };
};

type TSlotsFilterFactoryResult = {
  reducer: () => never;
  contextFunctions: TSlotsContextState;
};

const slotsSelectorWrapper = (placementSelector: (state: unknown) => TSlot[]) =>
  createSelector(placementSelector, (slots) => {
    const newArray = [];
    let ndx = 0;
    while (ndx < slots.length) {
      const { type } = slots[ndx];
      if (
        type === SLOT_TYPES.BANNER_SLOT_2 ||
        type === SLOT_TYPES.BANNER_SLOT
      ) {
        let same = ndx + 1;
        while (same < slots.length && type === slots[same].type) {
          same += 1;
        }
        const banners = slots.slice(ndx, same);
        ndx = same;

        if (type === SLOT_TYPES.BANNER_SLOT && banners.length > 1) {
          const newBanners = [];
          let mainBannerIndex = 0;
          let mergedBannerIndex = 1;
          while (mergedBannerIndex < banners.length) {
            const { slotHeader } = banners[mergedBannerIndex];

            // eslint-disable-next-line max-depth
            if (!slotHeader) {
              const bannerWithMissingHeader = banners[mergedBannerIndex];
              // eslint-disable-next-line max-depth
              if (mergedBannerIndex - mainBannerIndex === 1) {
                const mainBanner = { ...banners[mainBannerIndex] };
                newBanners.push({
                  ...mainBanner,
                  banners: [
                    ...(mainBanner.banners ?? []),
                    ...(bannerWithMissingHeader.banners ?? []),
                  ],
                });
              } else {
                const lastNewBanner = newBanners[newBanners.length - 1];
                lastNewBanner.banners = [
                  ...(lastNewBanner.banners ?? []),
                  ...(bannerWithMissingHeader.banners ?? []),
                ];
              }
            } else {
              // eslint-disable-next-line max-depth
              if (mergedBannerIndex - mainBannerIndex === 1) {
                newBanners.push(banners[mainBannerIndex]);
              }
              // eslint-disable-next-line max-depth
              if (mergedBannerIndex + 1 === banners.length) {
                newBanners.push(banners[mergedBannerIndex]);
              }
              mainBannerIndex = mergedBannerIndex;
            }
            mergedBannerIndex += 1;
          }
          newArray.push(newBanners);
        } else {
          newArray.push(banners);
        }
      } else {
        newArray.push(slots[ndx]);
        ndx += 1;
      }
    }
    return newArray;
  });

const homeSlotsFilters: TSlotsFilterFactoryResult = slotsFilteredFactory(
  SLOTS_PLACEMENT.HOME,
  slotsSelectorWrapper,
);
const diamondSlotsFilters: TSlotsFilterFactoryResult = slotsFilteredFactory(
  SLOTS_PLACEMENT.DIAMONDS,
  slotsSelectorWrapper,
);

export const { contextFunctions: homeContextFunctions } = homeSlotsFilters;
export const { contextFunctions: diamondsContextFunctions } =
  diamondSlotsFilters;

export const SlotsContext =
  createContext<TSlotsContextState>(homeContextFunctions);

export const useSlotsContext = (): TSlotsContextState =>
  useContext(SlotsContext);

export default {
  ...homeSlotsFilters.reducer,
  ...diamondSlotsFilters.reducer,
};
